/**
 * Cookie Consent Constants
 *
 * Centralized constants for the consent system.
 * All consent-related strings and defaults are defined here to avoid hardcoding.
 */

import type { ConsentState } from '../types/consent';

/**
 * localStorage key for storing consent preferences.
 * Using a namespaced key to avoid collisions.
 */
export const CONSENT_STORAGE_KEY = 'scheduledsmiles_consent';

/**
 * Current consent version.
 * Bump this when the consent UI or policy changes significantly
 * to re-prompt users who previously gave consent.
 */
export const CURRENT_CONSENT_VERSION = 2;

/**
 * Default consent state.
 * Non-essential categories default to false (opt-in required).
 */
export const DEFAULT_CONSENT: ConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
};

/**
 * UI text constants for the consent banner and modal.
 */
export const CONSENT_UI_TEXT = {
  /** Banner headline */
  bannerTitle: 'Cookie Preferences',
  
  /** Banner description — names the third-party processors that activate
   * with each non-essential category so users have informed consent
   * (required by GDPR and CCPA once advertising tags are loaded). */
  bannerDescription:
    'We use essential cookies to run the site. With your permission, we may also use analytics (Google Analytics) and marketing (Google Ads, Meta, OpenAI ChatGPT Ads) to improve and promote Scheduled Smiles.',
  
  /** Banner button labels */
  acceptAll: 'Accept All',
  essentialOnly: 'Essential Only',
  preferences: 'Cookie Preferences',
  
  /** Modal title */
  modalTitle: 'Cookie Preferences',
  
  /** Modal description - emphasize we never sell data */
  modalDescription:
    'Manage your cookie preferences below. We never sell your personal information to third parties.',
  
  /** Category labels */
  essentialLabel: 'Essential',
  essentialDescription: 'Required for core functionality. Always enabled.',
  
  analyticsLabel: 'Analytics',
  analyticsDescription:
    'Help us understand how visitors use the site (Google Analytics) so we can improve Scheduled Smiles.',

  marketingLabel: 'Marketing',
  marketingDescription:
    'If enabled, allows Google Ads, Meta, and OpenAI ChatGPT Ads to help promote Scheduled Smiles on other websites and to measure ad effectiveness. We never sell your personal information.',
  
  /** GPC notice */
  gpcNotice:
    'Your browser has Global Privacy Control enabled. Marketing is turned off to honor your preference.',
  
  /** Privacy notice - shown in modal for transparency */
  privacyNotice:
    'We never sell your personal information. You can change these preferences at any time.',
  
  /** Modal buttons */
  save: 'Save Preferences',
  cancel: 'Cancel',
  
  /** Footer link - single link for all privacy/cookie preferences */
  privacyChoicesLink: 'Privacy Choices',
} as const;
